import { Component, ErrorInfo, ReactNode } from 'react';
import { log } from './lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error('🔥 Error Boundary Caught:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to bottom right, #f8fafc, #e2e8f0)',
          padding: '20px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '800px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h1 style={{ color: '#dc2626', fontSize: '24px', marginBottom: '16px' }}>
              ⚠️ Application Error
            </h1>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              The application encountered an error and cannot continue. Please check the console for details.
            </p>

            {this.state.error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <strong style={{ color: '#991b1b' }}>Error:</strong>
                <pre style={{
                  color: '#7f1d1d',
                  fontSize: '14px',
                  marginTop: '8px',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {this.state.error.toString()}
                </pre>
              </div>
            )}

            {this.state.errorInfo && (
              <details style={{ marginBottom: '16px' }}>
                <summary style={{
                  cursor: 'pointer',
                  color: '#3b82f6',
                  marginBottom: '8px'
                }}>
                  Component Stack
                </summary>
                <pre style={{
                  background: '#f1f5f9',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  overflowX: 'auto',
                  color: '#475569'
                }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Reload Page
              </button>
              <button
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Clear Cache & Reload
              </button>
            </div>

            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#64748b'
            }}>
              <strong>Troubleshooting Tips:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Open browser DevTools (F12) and check the Console tab</li>
                <li>Try clearing your browser cache (Ctrl+Shift+Delete)</li>
                <li>Try opening in an incognito/private window</li>
                <li>Check if environment variables are set correctly</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
